import mixpanel from 'mixpanel-browser';
import { orgId, orgName } from '../selectors/organization';
import { getConnector } from './PatientFacingClient';
import { get, set, RESERVED_KEYS } from './SessionStore';

const TEN_MINUTES_TO_MS = 7 * 60 * 1000;
const trackedBrowserErrors = new Map();

let reportErrors = false;
let reportPerformance = false;

type Connection = { sessionId: string; updatedAt: number; count: number };

export const initializeTracker = (mixpanelKey: string, token: string | null) => {
  mixpanel.init(mixpanelKey);
  if (token) {
    mixpanel.identify(token);
  }
};

export const initializeReporter = (client) => {
  const urlParams = new URLSearchParams(window.location.search);
  const featureMatch = window.location.pathname.match(/feature\/([-\w]*)\/app\//);
  const localHostMatch = window.location.host.match('^(localhost|[0-9.]+):');

  reportErrors = urlParams.get('reportErrors') === 'true';
  reportPerformance = urlParams.get('reportPerformance') === 'true';

  if (['production', 'uat'].includes(client.config.apiEnv) && !featureMatch && !localHostMatch) {
    reportErrors = true;
    reportPerformance = true;
  }

  client.configure({
    reportErrors,
    reportPerformance,
  });

  if (reportErrors) overrideConsoleError();

  window.addEventListener('beforeunload', () => {
    const { client } = getConnector();
    const { sessionId } = client.report.__customFields;
    const storedConnections = get(RESERVED_KEYS.EVENT_CONNECTIONS) || {};
    delete storedConnections[sessionId];
    set(RESERVED_KEYS.EVENT_CONNECTIONS, storedConnections);
  });
};

export type MessageSentRecipientType = 'Provider' | 'Virtual Waiting Room Provider';
export type MixpanelVirtualWaitingRoomCallType = 'VWR Staff Call' | 'VWR Inform Provider';

export const trackMessageSent = (recipientType: MessageSentRecipientType = 'Provider') => {
  mixpanel.track('Message Sent', {
    'Is a TigerTouch Message': true,
    'Sender Type': getCurrentUserType(),
    'Type of Recipient': recipientType,
  });
};

export const trackMessageViewed = () => {
  mixpanel.track('Message Viewed', {
    'Is a TigerTouch Message': true,
    'Sender Type': 'Provider',
    'Type of Recipient': getCurrentUserType(),
  });
};

export const trackAuthenticationCodeRequested = () => {
  mixpanel.track('Request Authentication Code Tapped', {
    'Org Name': orgName,
    'Initial Session': true,
  });
};

export const trackSMSLinkOpened = () => {
  mixpanel.track('SMS Link Opened', {
    'Org name': orgName,
    'Initial Session': true,
  });
};

export const trackAuthenticationCodeEntered = (recipientId: string, userId: string) => {
  mixpanel.identify(userId);
  mixpanel.track('Authentication Code Entered', {
    'Recipient ID': recipientId,
    'Org Name': orgName,
    'Initial Session': true,
  });
};

export const trackVoipCallAnswered = (isVideo: boolean, isPatientLink = false) => {
  mixpanel.track(`VoIP ${isVideo ? 'Video' : 'Audio'} Call Answered`, {
    'Network Type': 'Patient',
    'Type of Recipient': 'P2P',
    'Forwarded Patient Link': isPatientLink,
  });
};

export const trackCallForwarded = ({ fromCopyLink, fromInCallBanner }) => {
  mixpanel.track('Forward Button Clicked', {
    'Forward Mechanism': fromCopyLink ? 'Copy Link' : 'Send Via Email',
    'Action Start': fromInCallBanner ? 'In-call banner' : 'Forward Button',
  });
};

export const trackVoipCallEnded = () => {
  mixpanel.track('VoIP Call Ended', {
    'Network Type': 'Patient',
    'Type of Recipient': 'P2P',
  });
};

export const trackVoipCallRejected = (isVideo: boolean) => {
  mixpanel.track(`VoIP ${isVideo ? 'Video' : 'Audio'} Call Rejected`, {
    'Network Type': 'Patient',
    'Type of Recipient': 'P2P',
  });
};

export const trackPFVideoBrowserError = (errorType: string) => {
  /* We track each error event only once per session */
  if (!trackedBrowserErrors.has(errorType)) {
    trackedBrowserErrors.set(errorType, true);
    mixpanel.track('Patient Video Browser Error', {
      'Error Type': errorType,
    });
  }

  if (reportErrors) {
    report({
      level: 'error',
      message: `PFVideoBrowserError: ${errorType}`,
    });
  }
};

export const trackSessionMigration = (migrationCount: number) => {
  if (reportPerformance && migrationCount) {
    report({
      level: 'info',
      message: 'Sessions Migrated',
      payload: {
        migrationCount,
      },
    });
  }
};

export const trackVirtualWaitingRoomConversationLoad = (
  organizationName: string,
  organizationId: string
) => {
  mixpanel.track('Patient Completed ID', {
    'Organization Name': organizationName,
    'Organization ID': organizationId,
  });
};

export const trackVirtualWaitingRoomIntakeFormLoad = (organizationName: string) => {
  mixpanel.track('Patient Entered Waiting Room', {
    'Organization Name': organizationName,
  });
};

export const trackVirtualWaitingRoomIntakeFormSubmission = ({
  patientName,
  patientPhoneNumber,
  reasonForVisit,
  consentGiven,
}: {
  patientName: string;
  patientPhoneNumber: string;
  reasonForVisit?: string;
  consentGiven: boolean;
}) => {
  mixpanel.track('Patient Entered Waiting Room', {
    'Patient Name': patientName,
    'Patient Phone Number': patientPhoneNumber,
    ...(reasonForVisit && { 'Patient Reason for Visit': reasonForVisit }),
    'Consent Given': `${consentGiven}`,
  });
};

export const trackVirtualWaitingRoomInformProviderModalButtonPressed = () => {
  mixpanel.track('VWR call invite card', {
    'Card button tapped': true,
  });
};

export const trackVirtualWaitingRoomCallJoined = (origin: MixpanelVirtualWaitingRoomCallType) => {
  mixpanel.track('VoIP Video Call Initiated', {
    'Organization Name': orgName,
    'Organization ID': orgId,
    'Call Origin': origin,
  });
};

export const trackVirtualWaitingRoomCallLeft = (origin: MixpanelVirtualWaitingRoomCallType) => {
  mixpanel.track('VoIP Video Call Ended', {
    'Organization Name': orgName,
    'Organization ID': orgId,
    'Call Origin': origin,
  });
};

const getCurrentUserType = () => {
  const { client } = getConnector();
  let currentUser = client.currentUser;
  let currentUserType = '';
  if (currentUser && currentUser.metadata && currentUser.metadata.length > 0) {
    currentUserType = currentUser.isPatientContact ? 'Patient Contact' : 'Patient';
  }
  return currentUserType;
};

const overrideConsoleError = () => {
  const consoleErrorFn = console.error || function () {};
  const consoleOnErrorFn = window.onerror || function () {};

  window.onerror = function (message, source, lineno, colno, error) {
    consoleOnErrorFn(message, source, lineno, colno, error);
    const stack = new Error().stack;

    report({
      level: 'error',
      message: 'onError',
      payload: { message, source, lineno, colno, error, stack },
    });
  };

  window.console.error = function (message) {
    consoleErrorFn(message);
    const errorStack = new Error().stack;

    report({
      level: 'error',
      message: 'consoleError',
      payload: {
        message,
        stack: errorStack,
      },
    });
  };
};

const report = (data) => {
  const { client } = getConnector();
  client.report.send(data);
};

const fetchConnectionStatus = () => {
  const storedConnections = get(RESERVED_KEYS.EVENT_CONNECTIONS) || {};
  const sessions = Object.keys(storedConnections);
  sessions.forEach((session) => {
    if (storedConnections[session].updatedAt + TEN_MINUTES_TO_MS < Date.now()) {
      delete storedConnections[session];
    }
  });

  set(RESERVED_KEYS.EVENT_CONNECTIONS, storedConnections);
  return storedConnections;
};

const saveConnectionStatus = (sessionId) => {
  const storedConnections = get(RESERVED_KEYS.EVENT_CONNECTIONS) || {};
  const storedConnection = storedConnections[sessionId] || {};
  storedConnections[sessionId] = {
    count: (storedConnection.count || 0) + 1,
    sessionId,
    updatedAt: Date.now(),
  };

  set(RESERVED_KEYS.EVENT_CONNECTIONS, storedConnections);
};

export const trackEventsConnection = () => {
  const { client } = getConnector();
  const { sessionId } = client.report.__customFields;
  saveConnectionStatus(sessionId);
  const connections = fetchConnectionStatus() as Connection[];
  let sessionIds: string[] = [];
  let updatedAt: number[] = [];
  let count: number[] = [];

  Object.values(connections).forEach((connection: Connection) => {
    sessionIds.push(connection.sessionId);
    updatedAt.push(connection.updatedAt);
    count.push(connection.count);
  });

  if (reportPerformance) {
    report({
      level: 'warn',
      message: 'openEventsData',
      payload: {
        connections: {
          sessionIds,
          updatedAt,
          count,
        },
      },
    });
  }
};
